import { Box, CardMedia } from '@material-ui/core';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppState, Comparison, Group, Project } from '../../../redux';
import { Body1, Header3, Header5, LinkButton } from '../../atoms';

import './multi-share-overview.styles.scss';

export interface IMultiShareOverviewStateProps {
	selectedProjects: Project[];
	selectedComparisons: Comparison[];
	selectedGroups: Group[];
}

export interface IMultiShareOverviewOwnProps {
	setStep: (step: number) => void;
}

export function MultiShareOverviewView(
	props: IMultiShareOverviewStateProps & IMultiShareOverviewOwnProps
): ReactElement {
	const { selectedProjects, selectedComparisons, selectedGroups, setStep } =
		props;

	const { t } = useTranslation('multiShare');

	const selectedStudentsCount: number = useMemo<number>(() => {
		return selectedGroups.flatMap((x) => x.students).length;
	}, [selectedGroups]);

	const selectionCount: number = useMemo<number>(() => {
		return selectedProjects.length + selectedComparisons.length;
	}, [selectedProjects, selectedComparisons]);

	const title: string | undefined = useMemo<string | undefined>(() => {
		if (selectionCount > 1 || selectionCount === 0) return undefined;
		if (selectedProjects.length > 0) return selectedProjects[0].title;
		return selectedComparisons[0].title;
	}, [selectedProjects, selectedComparisons, selectionCount]);

	const comparisonImages: string[] = useMemo<string[]>(() => {
		if (selectionCount > 1 || selectedComparisons.length !== 1) return [];
		const comparison: Comparison = selectedComparisons[0];
		const imageUrls: string[] = [
			comparison.firstAssetThumbnailUrl as string,
			comparison.secondAssetThumbnailUrl as string
		];
		if (comparison.thirdAssetThumbnailUrl)
			imageUrls.push(comparison.thirdAssetThumbnailUrl);
		if (comparison.fourthAssetThumbnailUrl)
			imageUrls.push(comparison.fourthAssetThumbnailUrl);

		return imageUrls;
	}, []);

	function getGridClassName(): string {
		switch (comparisonImages.length) {
			case 2:
				return 'grid-2';
			case 3:
				return 'grid-3';
			case 4:
				return 'grid-4';
			default:
				return '';
		}
	}

	function ComparisonThumbnail(): ReactElement {
		return (
			<Box className={`comparison-thumbnail ${getGridClassName()}`}>
				{comparisonImages.map((image: string, index: number) => (
					<Box
						key={`${index}-${image}`}
						className="comparison-img"
						style={{
							backgroundImage: `url('${image}')`
						}}
					/>
				))}
			</Box>
		);
	}

	return (
		<Box className="multi-share-overview">
			<Header3>{t('checkComplete')}</Header3>
			<Box className="multi-share-overview-content">
				<Box className="multi-share-overview-data">
					{selectionCount === 1 && (
						<>
							<Header5 className="no-margin">
								{t('title')}
							</Header5>
							<Body1>{title}</Body1>
						</>
					)}
					<Header5>{t('selection')}</Header5>
					<Body1>
						{t('amountMaps', { amount: selectedProjects.length })}
					</Body1>
					<Body1>
						{t('amountComparisons', {
							amount: selectedComparisons.length
						})}
					</Body1>
					<LinkButton onClick={() => setStep(0)}>
						{t('change')}
					</LinkButton>
					<Header5>{t('subTitleStudentesClasses')}</Header5>
					<Body1>
						{t('studentsInClasses', {
							amountStudents: selectedStudentsCount,
							amountClasses: selectedGroups.length
						})}
					</Body1>
					<LinkButton onClick={() => setStep(1)}>
						{t('change')}
					</LinkButton>
				</Box>
				{selectionCount === 1 && (
					<Box className="multi-share-overview-preview">
						<Header5>{t('previewMap')}</Header5>
						{selectedProjects.length > 0 && (
							<CardMedia
								component="img"
								image={selectedProjects[0].thumbnailUrl}
							/>
						)}
						{selectedComparisons.length > 0 && (
							<ComparisonThumbnail />
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
}

function mapStateToProps(state: AppState) {
	return {
		selectedProjects: state.multiShare.selectedProjects,
		selectedComparisons: state.multiShare.selectedComparisons,
		selectedGroups: state.multiShare.selectedGroups
	};
}

export const MultiShareOverview = connect(mapStateToProps)(
	MultiShareOverviewView
);
